import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Highlighter, Languages, Save, X, PenLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { auth, db } from '../../firebase';
import enhancedUserService from '../../services/enhancedUserService';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const STORAGE_PREFIX = 'reading_word_tools_v3';
const MAX_TERM_LENGTH = 64;

const TARGET_LANG_MAP = {
  en: 'en',
  de: 'de',
  fr: 'fr',
  es: 'es',
  tr: 'tr',
  ru: 'ru'
};

function normalizeWhitespace(value) {
  return value.replace(/\s+/g, ' ').trim();
}

function normalizeTerm(value) {
  const trimmed = normalizeWhitespace(String(value || ''));
  if (!trimmed) return '';
  if (trimmed.length > MAX_TERM_LENGTH) return '';
  return trimmed;
}

function sanitizeSelectedText(value) {
  const normalized = normalizeTerm(value);
  if (!normalized) return '';
  if (!/[\p{L}\p{N}]/u.test(normalized)) return '';
  return normalized;
}

function normalizeTermKey(value) {
  return normalizeTerm(value).toLocaleLowerCase('de-DE');
}

function isEligibleTextNode(node) {
  const parent = node?.parentElement;
  if (!parent) return false;
  if (!node.nodeValue || !node.nodeValue.trim()) return false;
  if (parent.closest('[data-reading-tools-ui="1"]')) return false;
  return true;
}

function getTextNodeOffsetMap(root) {
  const map = new Map();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let consumed = 0;

  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!isEligibleTextNode(node)) continue;
    map.set(node, consumed);
    consumed += node.nodeValue.length;
  }

  return map;
}

function getSelectionOffsets(root, range) {
  if (!root || !range) return null;

  if (range.startContainer.nodeType !== Node.TEXT_NODE || range.endContainer.nodeType !== Node.TEXT_NODE) {
    return null;
  }

  const startNode = range.startContainer;
  const endNode = range.endContainer;
  if (!isEligibleTextNode(startNode) || !isEligibleTextNode(endNode)) return null;

  const offsets = getTextNodeOffsetMap(root);
  const startBase = offsets.get(startNode);
  const endBase = offsets.get(endNode);
  if (!Number.isFinite(startBase) || !Number.isFinite(endBase)) return null;

  const start = startBase + range.startOffset;
  const end = endBase + range.endOffset;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;

  return { start, end };
}

function getTrimmedSelection(root, range) {
  if (!root || !range) return null;

  const rawText = String(range.toString() || '');
  if (!rawText) return null;

  const offsets = getSelectionOffsets(root, range);
  if (!offsets) return null;

  const leadingWhitespace = rawText.match(/^\s+/u)?.[0]?.length || 0;
  const trailingWhitespace = rawText.match(/\s+$/u)?.[0]?.length || 0;
  const start = offsets.start + leadingWhitespace;
  const end = offsets.end - trailingWhitespace;

  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;

  const selectedText = sanitizeSelectedText(rawText.trim());
  if (!selectedText) return null;

  return {
    text: selectedText,
    start,
    end
  };
}

function locateTextPoint(root, targetOffset) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let consumed = 0;
  let lastEligibleNode = null;

  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!isEligibleTextNode(node)) continue;

    lastEligibleNode = node;
    const length = node.nodeValue.length;
    const nextConsumed = consumed + length;

    if (targetOffset <= nextConsumed) {
      return {
        node,
        offset: Math.max(0, Math.min(length, targetOffset - consumed))
      };
    }

    consumed = nextConsumed;
  }

  if (lastEligibleNode) {
    return { node: lastEligibleNode, offset: lastEligibleNode.nodeValue.length };
  }

  return null;
}

function normalizeHighlightRanges(rawRanges) {
  const normalized = (rawRanges || [])
    .map((item) => {
      const start = Number(item?.start);
      const end = Number(item?.end);
      const text = normalizeTerm(item?.text || '');
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || !text) return null;

      return {
        id: `${start}:${end}`,
        start,
        end,
        text
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start);

  const nonOverlapping = [];
  let lastEnd = -1;
  normalized.forEach((range) => {
    if (range.start < lastEnd) return;
    nonOverlapping.push(range);
    lastEnd = range.end;
  });

  return nonOverlapping;
}

function buildPartSignature(partData) {
  const content = partData?.content || {};
  const textPool = [
    partData?.partId,
    partData?.taskType,
    partData?.partName,
    partData?.examProvider,
    partData?.level,
    content?.mainText,
    Array.isArray(content?.texts) ? content.texts.map((t) => `${t?.id || ''}|${t?.title || ''}|${t?.content || ''}`).join('||') : '',
    Array.isArray(content?.options) ? content.options.map((o) => `${o?.id || ''}|${o?.text || ''}`).join('||') : ''
  ].join('::');

  let hash = 0;
  for (let i = 0; i < textPool.length; i += 1) {
    hash = ((hash << 5) - hash + textPool.charCodeAt(i)) | 0;
  }
  return String(hash);
}

function unwrapHighlights(root) {
  const highlighted = root.querySelectorAll('span[data-reading-highlight="1"]');
  highlighted.forEach((span) => {
    const parent = span.parentNode;
    if (!parent) return;
    parent.replaceChild(document.createTextNode(span.textContent || ''), span);
    parent.normalize();
  });
}

function applyHighlights(root, rawRanges) {
  if (!root) return;

  unwrapHighlights(root);

  const ranges = normalizeHighlightRanges(rawRanges);
  if (!ranges.length) return;

  ranges.forEach((item) => {
    const startPoint = locateTextPoint(root, item.start);
    const endPoint = locateTextPoint(root, item.end);
    if (!startPoint || !endPoint) return;

    const range = document.createRange();
    try {
      range.setStart(startPoint.node, startPoint.offset);
      range.setEnd(endPoint.node, endPoint.offset);
      if (range.collapsed) return;

      const renderedText = normalizeTerm(range.toString());
      const expectedText = normalizeTerm(item.text);
      if (!renderedText || !expectedText || renderedText !== expectedText) return;

      const span = document.createElement('span');
      span.dataset.readingHighlight = '1';
      span.dataset.readingHighlightId = item.id;
      span.dataset.readingHighlightKey = normalizeTermKey(item.text);
      span.className = 'rounded-sm bg-amber-200/90 text-gray-900 px-[2px] py-[1px]';

      const fragment = range.extractContents();
      span.appendChild(fragment);
      range.insertNode(span);
    } catch {
      // Ignore invalid ranges after content changes.
    }
  });
}

async function translateTerm(term, targetLanguage) {
  const sourceLanguage = 'de';
  const target = targetLanguage === 'de' ? 'en' : targetLanguage;
  const endpoint = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(term)}&langpair=${sourceLanguage}|${target}`;

  const response = await fetch(endpoint);
  if (!response.ok) throw new Error('translation_failed');

  const data = await response.json();
  const translated = normalizeWhitespace(data?.responseData?.translatedText || '');
  if (!translated) throw new Error('translation_empty');

  return translated;
}

async function saveWordToHighlightsDeck(userId, term, translation = '') {
  const deckRef = doc(db, `users/${userId}/vocabulary_decks/reading_highlights`);
  const snapshot = await getDoc(deckRef);

  const nowIso = new Date().toISOString();
  const existingDeck = snapshot.exists() ? snapshot.data() : {};
  const existingCards = Array.isArray(existingDeck.cards) ? existingDeck.cards : [];

  const existingIndex = existingCards.findIndex(
    (card) => normalizeTermKey(card?.front || card?.german || '') === normalizeTermKey(term)
  );

  const nextCard = {
    front: term,
    back: translation || '',
    german: term,
    english: translation || '',
    createdAt: nowIso,
    source: 'reading-practice'
  };

  let cards;
  if (existingIndex >= 0) {
    cards = [...existingCards];
    cards[existingIndex] = {
      ...cards[existingIndex],
      ...nextCard,
      createdAt: cards[existingIndex]?.createdAt || nowIso
    };
  } else {
    cards = [...existingCards, nextCard];
  }

  await setDoc(deckRef, {
    id: 'reading_highlights',
    name: 'Reading Highlights',
    icon: 'PEN',
    isOfficial: false,
    cards,
    createdAt: existingDeck.createdAt || nowIso,
    updatedAt: nowIso
  }, { merge: true });
}

const ReadingWordTools = ({ enabled = false, containerRef, partData }) => {
  const { t, i18n } = useTranslation();
  const [isMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  const [markerMode, setMarkerMode] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedSelection, setSelectedSelection] = useState(null);
  const [toolbarPos, setToolbarPos] = useState(null);
  const [highlightedRanges, setHighlightedRanges] = useState([]);
  const [translations, setTranslations] = useState({});
  const [status, setStatus] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showMarkerHint, setShowMarkerHint] = useState(true);
  const isApplyingRef = useRef(false);

  const userId = auth.currentUser?.uid || 'guest';
  const partSignature = useMemo(() => buildPartSignature(partData), [partData]);
  const storageKey = useMemo(() => `${STORAGE_PREFIX}:${userId}:${partSignature}`, [partSignature, userId]);
  const currentLang = (i18n.language || 'en').split('-')[0];
  const targetLang = TARGET_LANG_MAP[currentLang] || 'en';
  const selectedKey = normalizeTermKey(selectedTerm);
  const selectedHighlightId = selectedSelection ? `${selectedSelection.start}:${selectedSelection.end}` : null;
  const isHighlighted = Boolean(selectedHighlightId) && highlightedRanges.some((item) => item.id === selectedHighlightId);
  const translationKey = `${selectedKey}:${targetLang}`;
  const translationText = translations[translationKey] || '';

  useEffect(() => {
    if (!enabled) return;

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        setHighlightedRanges([]);
        setTranslations({});
        return;
      }

      const parsed = JSON.parse(raw);
      setHighlightedRanges(normalizeHighlightRanges(parsed?.highlightedRanges));
      setTranslations(parsed?.translations && typeof parsed.translations === 'object' ? parsed.translations : {});
    } catch (error) {
      console.error('Failed to load reading word tools state:', error);
      setHighlightedRanges([]);
      setTranslations({});
    }
  }, [enabled, storageKey]);

  useEffect(() => {
    if (!enabled) return;

    try {
      localStorage.setItem(storageKey, JSON.stringify({ highlightedRanges, translations }));
    } catch (error) {
      console.error('Failed to persist reading word tools state:', error);
    }
  }, [enabled, storageKey, highlightedRanges, translations]);

  useEffect(() => {
    if (!enabled || !containerRef?.current) return;

    const root = containerRef.current;
    const apply = () => {
      if (isApplyingRef.current) return;
      isApplyingRef.current = true;
      applyHighlights(root, highlightedRanges);
      isApplyingRef.current = false;
    };

    apply();

    const observer = new MutationObserver((mutationList) => {
      const contentMutated = mutationList.some((mutation) => {
        const target = mutation.target;
        return !(target instanceof Element) || !target.closest('[data-reading-tools-ui="1"]');
      });
      if (!contentMutated || isApplyingRef.current) return;
      window.requestAnimationFrame(apply);
    });

    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [enabled, containerRef, highlightedRanges]);

  useEffect(() => {
    if (!enabled || !containerRef?.current) return;

    const root = containerRef.current;
    const captureSelection = () => {
      if (!markerMode) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

      const anchorNode = selection.anchorNode;
      const focusNode = selection.focusNode;
      if (!anchorNode || !focusNode) return;

      const anchorElement = anchorNode.nodeType === Node.ELEMENT_NODE ? anchorNode : anchorNode.parentElement;
      const focusElement = focusNode.nodeType === Node.ELEMENT_NODE ? focusNode : focusNode.parentElement;
      if (anchorElement?.closest('[data-reading-tools-ui="1"]') || focusElement?.closest('[data-reading-tools-ui="1"]')) return;
      if (anchorElement?.closest('button, input, textarea, select, [contenteditable="true"]')) return;
      if (focusElement?.closest('button, input, textarea, select, [contenteditable="true"]')) return;

      if (!root.contains(anchorNode) || !root.contains(focusNode)) return;

      const range = selection.getRangeAt(0);
      const trimmedSelection = getTrimmedSelection(root, range);
      if (!trimmedSelection) {
        setToolbarPos(null);
        setSelectedTerm('');
        setSelectedSelection(null);
        setStatus(t('readingTools.singleWordOnly'));
        return;
      }

      const rect = range.getBoundingClientRect();
      setToolbarPos({
        top: Math.max(16, rect.top - 58),
        left: Math.max(12, Math.min(window.innerWidth - 300, rect.left + rect.width / 2 - 140))
      });
      setSelectedTerm(trimmedSelection.text);
      setSelectedSelection({ start: trimmedSelection.start, end: trimmedSelection.end });
    };

    const handlePointerUp = () => {
      window.requestAnimationFrame(captureSelection);
    };

    root.addEventListener('pointerup', handlePointerUp);
    return () => root.removeEventListener('pointerup', handlePointerUp);
  }, [enabled, containerRef, markerMode, t]);

  useEffect(() => {
    if (!status) return undefined;
    const timer = window.setTimeout(() => setStatus(''), 2200);
    return () => window.clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    if (!markerMode) {
      setToolbarPos(null);
      setSelectedTerm('');
      setSelectedSelection(null);
    }
  }, [markerMode]);

  const handleToggleHighlight = () => {
    if (!selectedTerm || !selectedSelection) return;

    const nextId = `${selectedSelection.start}:${selectedSelection.end}`;

    if (isHighlighted) {
      setHighlightedRanges((prev) => prev.filter((item) => item.id !== nextId));
      setStatus(t('readingTools.removed'));
      return;
    }

    setHighlightedRanges((prev) => {
      if (prev.some((item) => item.id === nextId)) return prev;
      return normalizeHighlightRanges([
        ...prev,
        {
          start: selectedSelection.start,
          end: selectedSelection.end,
          text: selectedTerm
        }
      ]);
    });
    setStatus(t('readingTools.highlighted'));
  };

  const handleSaveWord = async () => {
    if (!selectedTerm || isSaving) return;

    const uid = auth.currentUser?.uid;
    if (!uid) {
      setStatus(t('readingTools.loginRequired'));
      return;
    }

    try {
      setIsSaving(true);
      const payload = [{
        german: selectedTerm,
        translation: translationText || '',
        source: 'reading-practice',
        partId: partData?.partId || '',
        partName: partData?.partName || ''
      }];

      const [vocabResult] = await Promise.all([
        enhancedUserService.trackVocabulary(uid, payload, 'learned'),
        saveWordToHighlightsDeck(uid, selectedTerm, translationText || '')
      ]);

      if (vocabResult && vocabResult.success === false) {
        throw new Error(vocabResult.error || 'save_failed');
      }

      setStatus(t('readingTools.saved'));
    } catch (error) {
      console.error('Failed to save selected word:', error);
      setStatus(t('readingTools.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleTranslate = async () => {
    if (!selectedTerm || isTranslating) return;

    try {
      setIsTranslating(true);
      const translated = await translateTerm(selectedTerm, targetLang);
      setTranslations((prev) => ({
        ...prev,
        [translationKey]: translated
      }));
    } catch (error) {
      console.error('Failed to translate selected word:', error);
      setStatus(t('readingTools.translationError'));
    } finally {
      setIsTranslating(false);
    }
  };

  if (!enabled || isMobile) return null;

  return (
    <>
      <div data-reading-tools-ui="1" className="fixed bottom-6 right-6 z-40">
        <button
          type="button"
          className={`min-h-[48px] rounded-full border px-4 py-2.5 text-xs font-bold shadow-[0_10px_24px_rgba(15,23,42,0.2)] transition-all duration-300 touch-manipulation ${
            markerMode
              ? 'border-amber-400 bg-amber-100 text-amber-900'
              : 'border-black/10 bg-white text-gray-700 hover:bg-gray-50'
          }`}
          onClick={() => {
            setMarkerMode((prev) => !prev);
            setShowMarkerHint(false);
            setStatus('');
          }}
          aria-pressed={markerMode}
          aria-label={t('readingTools.markerToggle')}
        >
          <span className="inline-flex items-center gap-1.5">
            <PenLine className="h-4 w-4" />
            {markerMode ? t('readingTools.markerOn') : t('readingTools.markerOff')}
          </span>
        </button>

        {showMarkerHint && !markerMode && (
          <p className="mt-2 max-w-[220px] rounded-lg border border-black/10 bg-white px-2.5 py-2 text-[11px] font-semibold text-gray-600 shadow-sm">
            {t('readingTools.markerHint')}
          </p>
        )}

        {status && !toolbarPos && (
          <p className="mt-2 rounded-lg border border-black/10 bg-white px-2.5 py-2 text-[11px] font-semibold text-gray-600 shadow-sm">
            {status}
          </p>
        )}
      </div>

      {markerMode && toolbarPos && selectedTerm && (
        <div
          data-reading-tools-ui="1"
          className="fixed z-50 w-[280px] max-w-[calc(100vw-24px)] rounded-xl border border-black/10 bg-white/95 p-3 shadow-[0_14px_28px_rgba(15,23,42,0.2)] backdrop-blur"
          style={{ top: `${toolbarPos.top}px`, left: `${toolbarPos.left}px` }}
          role="dialog"
          aria-label={t('readingTools.toolbarLabel')}
        >
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs font-semibold text-gray-900 break-words leading-snug">"{selectedTerm}"</p>
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-black/10 text-gray-500 hover:bg-gray-100"
              onClick={() => {
                setToolbarPos(null);
                setSelectedTerm('');
                window.getSelection()?.removeAllRanges();
              }}
              aria-label={t('readingTools.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-2 grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={handleToggleHighlight}
              className="min-h-[44px] rounded-lg border border-amber-300 bg-amber-50 px-2 text-[11px] font-bold text-amber-800 transition-colors hover:bg-amber-100"
            >
              <span className="inline-flex items-center justify-center gap-1">
                <Highlighter className="h-3.5 w-3.5" />
                {isHighlighted ? t('readingTools.removeHighlight') : t('readingTools.highlight')}
              </span>
            </button>

            <button
              type="button"
              onClick={handleSaveWord}
              disabled={isSaving}
              className="min-h-[44px] rounded-lg border border-[#1f3b63]/25 bg-[#eef3fb] px-2 text-[11px] font-bold text-[#1f3b63] transition-colors hover:bg-[#e2ebf8] disabled:opacity-50"
            >
              <span className="inline-flex items-center justify-center gap-1">
                <Save className="h-3.5 w-3.5" />
                {t('readingTools.save')}
              </span>
            </button>

            <button
              type="button"
              onClick={handleTranslate}
              disabled={isTranslating}
              className="min-h-[44px] rounded-lg border border-emerald-300 bg-emerald-50 px-2 text-[11px] font-bold text-emerald-800 transition-colors hover:bg-emerald-100 disabled:opacity-50"
            >
              <span className="inline-flex items-center justify-center gap-1">
                <Languages className="h-3.5 w-3.5" />
                {isTranslating ? t('readingTools.translating') : t('readingTools.translate')}
              </span>
            </button>
          </div>

          {translationText && (
            <p className="mt-2 rounded-lg bg-gray-50 px-2.5 py-2 text-xs text-gray-700">
              <span className="font-semibold text-gray-900">{t('readingTools.translation')}</span> {translationText}
            </p>
          )}

          {status && <p className="mt-2 text-[11px] font-semibold text-gray-600">{status}</p>}
        </div>
      )}
    </>
  );
};

export default ReadingWordTools;
